import { Position, Unit, TerrainType, FactionType, UnitType } from './types';
import { GameMap } from './Map';
import { UnitManager } from './UnitManager';
import { isAircraft } from './unitDefinitions';

export class VisionSystem {
  private visibilityMap: Map<string, boolean> = new Map();
  private visionCache: Map<string, Set<string>> = new Map();
  
  // TESTING FLAG: Set to true to disable fog of war
  private static readonly DISABLE_FOG_OF_WAR = false;
  
  constructor() {}
  
  private posToKey(x: number, y: number): string {
    return `${x},${y}`;
  }
  
  /*private keyToPos(key: string): Position {
    const [x, y] = key.split(',').map(Number);
    return { x, y };
  }*/
  
  calculateVisibility(
    map: GameMap,
    unitManager: UnitManager,
    currentFaction: FactionType
  ): Map<string, boolean> {
    this.visibilityMap.clear();
    
    // Get all friendly units
    const friendlyUnits = unitManager.getUnitsForFaction(currentFaction);
    
    // Calculate vision from each unit
    for (const unit of friendlyUnits) {
      const visionTiles = this.calculateUnitVision(unit, map);
      for (const tileKey of visionTiles) {
        this.visibilityMap.set(tileKey, true);
      }
    }
    
    // Calculate vision from friendly cities
    const cities = map.getCities();
    for (const city of cities.values()) {
      if (city.faction === currentFaction) {
        const cityVision = this.calculateCityVision(city, map);
        for (const tileKey of cityVision) {
          this.visibilityMap.set(tileKey, true);
        }
      }
    }
    
    return this.visibilityMap;
  }
  
  private calculateUnitVision(unit: Unit, map: GameMap): Set<string> {
    const cacheKey = `${unit.id}-${unit.position.x},${unit.position.y}`;
    
    // Check cache first
    if (this.visionCache.has(cacheKey)) {
      return this.visionCache.get(cacheKey)!;
    }
    
    const visibleTiles = new Set<string>();
    // const unitDef = UNIT_DEFINITIONS[unit.type];
    let baseRange = this.getUnitVisionRange(unit.type);
    
    // Check if unit is on a hill or ridge for vision bonus
    const unitTile = map.getTile(unit.position.x, unit.position.y);
    // Hills and ridges give vision bonus in new system
    if (unitTile && (unitTile.terrain === TerrainType.CORAL_RIDGES || 
                     unitTile.terrain === TerrainType.ROCKY_HIGHLANDS ||
                     unitTile.terrain === TerrainType.MOUNTAIN_RIDGES ||
                     unitTile.terrain === TerrainType.WOODED_HILLS)) {
      baseRange += 2; // +2 vision range when on elevated terrain
    }
    
    // Use a simple line-of-sight algorithm with terrain blocking
    for (let dy = -baseRange; dy <= baseRange; dy++) {
      for (let dx = -baseRange; dx <= baseRange; dx++) {
        const targetX = unit.position.x + dx;
        const targetY = unit.position.y + dy;
        
        // Check if within range (using Chebyshev distance - square pattern)
        if (Math.abs(dx) > baseRange || Math.abs(dy) > baseRange) continue;
        
        // Check if tile exists
        const targetTile = map.getTile(targetX, targetY);
        if (!targetTile) continue;
        
        // Check line of sight
        if (this.hasLineOfSight(unit.position, { x: targetX, y: targetY }, map, baseRange)) {
          visibleTiles.add(this.posToKey(targetX, targetY));
        }
      }
    }
    
    // Always include the unit's own tile
    visibleTiles.add(this.posToKey(unit.position.x, unit.position.y));
    
    // Cache the result
    this.visionCache.set(cacheKey, visibleTiles);
    
    return visibleTiles;
  }
  
  private calculateCityVision(city: any, map: GameMap): Set<string> {
    const visibleTiles = new Set<string>();
    const cityVisionRange = 4;
    
    // Use the center tile of the city
    if (city.tiles.length === 0) return visibleTiles;
    const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
    
    // Similar to unit vision but from city center
    for (let dy = -cityVisionRange; dy <= cityVisionRange; dy++) {
      for (let dx = -cityVisionRange; dx <= cityVisionRange; dx++) {
        const targetX = centerTile.x + dx;
        const targetY = centerTile.y + dy;
        
        if (Math.abs(dx) > cityVisionRange || Math.abs(dy) > cityVisionRange) continue;
        
        const targetTile = map.getTile(targetX, targetY);
        if (!targetTile) continue;
        
        if (this.hasLineOfSight(centerTile, { x: targetX, y: targetY }, map, cityVisionRange)) {
          visibleTiles.add(this.posToKey(targetX, targetY));
        }
      }
    }
    
    // Always include all city tiles
    for (const tile of city.tiles) {
      visibleTiles.add(this.posToKey(tile.x, tile.y));
    }
    
    return visibleTiles;
  }
  
  private hasLineOfSight(from: Position, to: Position, map: GameMap, maxRange: number): boolean {
    // If it's the same tile, always visible
    if (from.x === to.x && from.y === to.y) return true;
    
    // Use Bresenham's line algorithm to check tiles between points
    const dx = Math.abs(to.x - from.x);
    const dy = Math.abs(to.y - from.y);
    const sx = from.x < to.x ? 1 : -1;
    const sy = from.y < to.y ? 1 : -1;
    let err = dx - dy;
    
    let x = from.x;
    let y = from.y;
    
    // Track vision reduction from rough terrain
    let distanceFromSource = 0;
    let effectiveRange = maxRange;
    const fromTile = map.getTile(from.x, from.y);
    const isSourceOnHill = fromTile && (fromTile.terrain === TerrainType.CORAL_RIDGES || 
                                        fromTile.terrain === TerrainType.ROCKY_HIGHLANDS ||
                                        fromTile.terrain === TerrainType.MOUNTAIN_RIDGES ||
                                        fromTile.terrain === TerrainType.WOODED_HILLS);
    
    while (x !== to.x || y !== to.y) {
      const e2 = 2 * err;
      
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
      
      distanceFromSource++;
      
      // Check the tile we're passing through
      const tile = map.getTile(x, y);
      if (!tile) return false;
      
      // Don't check the destination tile for blocking
      if (x === to.x && y === to.y) break;
      
      // Rough terrain partially blocks vision
      if (tile.terrain === TerrainType.BOCAGE ||
          tile.terrain === TerrainType.ROCKY_HIGHLANDS ||
          tile.terrain === TerrainType.SNOW_DRIFTS ||
          tile.terrain === TerrainType.CORAL_RIDGES ||
          tile.terrain === TerrainType.WOODED_HILLS) {
        effectiveRange -= 1; // -1 vision for each rough terrain tile
        
        // If we've exceeded our effective range, can't see through
        if (distanceFromSource >= effectiveRange) {
          return false;
        }
      }
      
      // Challenging terrain blocks vision significantly more
      if (tile.terrain === TerrainType.DENSE_WOODS ||
          tile.terrain === TerrainType.SHIFTING_DUNES ||
          tile.terrain === TerrainType.FROZEN_MARSHES ||
          tile.terrain === TerrainType.DENSE_JUNGLE ||
          tile.terrain === TerrainType.MOUNTAIN_RIDGES) {
        effectiveRange -= 2; // -2 vision for each challenging terrain tile
        
        // If we've exceeded our effective range, can't see through
        if (distanceFromSource >= effectiveRange) {
          return false;
        }
      }
      
      // Elevated terrain blocks vision if you're not also on elevated terrain
      if ((tile.terrain === TerrainType.CORAL_RIDGES || 
           tile.terrain === TerrainType.ROCKY_HIGHLANDS ||
           tile.terrain === TerrainType.MOUNTAIN_RIDGES ||
           tile.terrain === TerrainType.WOODED_HILLS) && !isSourceOnHill) {
        // Elevated terrain in the middle of the path blocks vision completely
        if (x !== from.x || y !== from.y) {
          return false;
        }
      }
    }
    
    return true;
  }
  
  private getUnitVisionRange(unitType: string): number {
    // Aircraft get recon-level vision range
    if (isAircraft(unitType as UnitType)) {
      return 5;
    }
    
    // Vision ranges as specified in Fog.md
    switch (unitType) {
      // Infantry units: 3 tiles
      case 'rifle_squad':
      case 'ranger_squad':
      case 'airborne_squad':
      case 'waffen_ss_squad':
      case 'fallschirmjager_squad':
      case 'panzergrenadier_squad':
      case 'infantry_section':
      case 'guards_infantry':
      case 'commando_section':
      case 'schutzen_squad':
      case 'afrika_grenadiers':
      case 'ak_fallschirmjager':
        return 3;
        
      // Tank units: 2 tiles
      case 'sherman_section':
      case 'tank_destroyer_section':
      case 'panzer_iv_zug':
      case 'panther_section':
      case 'stug_iii_section':
      case 'crusader_tank':
      case 'matilda_ii':
      case 'panzer_iii':
      case 'panzer_ii':
      case 'six_pounder_at':
      case 'pak_38':
        return 2;
        
      // Artillery: 4 tiles
      case 'us_artillery_section':
      case 'ger_artillery_section':
      case 'twenty_five_pounder':
      case 'eighty_eight_flak':
        return 4;
        
      // Engineers: 2 tiles
      case 'engineer_squad':
      case 'pioneer_squad':
      case 'sapper_section':
      case 'ak_pioneer_squad':
        return 2;
        
      // Recon units: 5 tiles (highest vision)
      case 'm8_greyhound':
      case 'sdkfz_222':
      case 'daimler_dingo':
      case 'sdkfz_250_9':
      case 'reconnaissance_patrol':
      case 'arditi_scouts':
        return 5;
        
      default:
        return 2;
    }
  }
  
  isPositionVisible(x: number, y: number): boolean {
    // TESTING: Return true to disable fog of war
    if (VisionSystem.DISABLE_FOG_OF_WAR) {
      return true;
    }
    return this.visibilityMap.has(this.posToKey(x, y));
  }
  
  clearCache(): void {
    this.visionCache.clear();
  }
}